/**
 * waitingListMessageUtils.js
 *
 * Utilitário centralizado para geração de mensagens de oferta de horário
 * da lista de espera. Todos os lugares que oferecem horários via WhatsApp
 * devem usar esta função para garantir consistência de formato e texto.
 *
 * Formato padrão da mensagem (revisão
 * docs/features/whatsapp-shared-number/revisao-mensagens-padrao.md, item 18):
 *   "*{company_name}*
 *
 *    Olá, {name}! Você está na lista de espera e surgiu uma vaga:
 *
 *    Data: {when}, {day} de {month}, às {time}
 *    Profissional: {professional_name}
 *    Serviço: {procedure_name}
 *
 *    Quer esse horário? Responda o quanto antes, pois a vaga pode ser oferecida a outra pessoa."
 */

import MessagesTemplate from '../consts/MessagesTemplate';
import CalendarConfig from '../consts/CalendarConfig';

const FALLBACK_TEMPLATE =
  '*{company_name}*\n\n' +
  'Olá, {name}! Você está na lista de espera e surgiu uma vaga:\n\n' +
  'Data: {when}, {day} de {month}, às {time}\n' +
  'Profissional: {professional_name}\n' +
  'Serviço: {procedure_name}\n\n' +
  'Quer esse horário? Responda o quanto antes, pois a vaga pode ser oferecida a outra pessoa.';

/**
 * Substitui as variáveis do template, removendo antes a linha inteira de
 * qualquer variável opcional que venha vazia (ex.: "Profissional: {professional_name}"
 * some do texto quando não há profissional definido). Mesmo comportamento do
 * backend em substituteTemplateVariables()
 * (public/api/automations/message_template_functions.php).
 *
 * @param {string} template
 * @param {Object} variables - Mapa variável -> valor
 * @param {string[]} optionalKeys - Variáveis cuja linha "Rótulo: {var}" deve sumir se vazias
 * @returns {string}
 */
function applyTemplateVariables(template, variables, optionalKeys = []) {
  let result = template;

  optionalKeys.forEach((key) => {
    const value = variables[key];
    const trimmed = value ? String(value).trim() : '';
    if (trimmed === '' && result.includes(`{${key}}`)) {
      const lineRegex = new RegExp(`^[^\\n{}]{1,40}:[ \\t]*\\{${key}\\}[ \\t]*\\r?\\n?`, 'm');
      result = result.replace(lineRegex, '');
    }
  });

  Object.entries(variables).forEach(([key, value]) => {
    result = result.replaceAll(`{${key}}`, value != null ? String(value) : '');
  });

  // Colapsa 3+ quebras de linha consecutivas (deixadas pela remoção de
  // linhas opcionais acima) em no máximo uma linha em branco.
  result = result.replace(/\n{3,}/g, '\n\n');

  return result;
}

/**
 * Constrói a mensagem de oferta de horário da lista de espera.
 *
 * Usa o template `SuggestDateFromWaitingList` configurado na empresa.
 * Se o template não estiver disponível, usa um fallback com o formato padrão.
 *
 * @param {Object} params
 * @param {string} params.patientName       - Nome completo do cliente (usa apenas o primeiro nome)
 * @param {string} params.slotDate          - Data do slot no formato "YYYY-MM-DD"
 * @param {string} params.slotTime          - Hora do slot no formato "HH:MM"
 * @param {string} [params.resourceLabel]   - Nome do profissional (ex: "Dra. Juliana Nakashima")
 * @param {string} [params.procedureName]   - Nome do serviço/procedimento (ex: "Osteopatia")
 * @param {Object} [params.companyConfig]   - Configuração da empresa (para buscar o template)
 * @returns {string} Mensagem formatada pronta para envio via WhatsApp
 */
export function buildWaitingListOfferMessage({
  patientName,
  slotDate,
  slotTime,
  resourceLabel = '',
  procedureName = '',
  companyConfig = null,
}) {
  const firstName = patientName ? patientName.trim().split(' ')[0] : 'cliente';

  // Formata a data
  const d = new Date(`${slotDate}T${slotTime || '08:00'}:00`);
  const day = d.getDate();
  const month = CalendarConfig.Months[d.getMonth()]; // ex: "Julho"
  const weekday = d.toLocaleDateString('pt-BR', { weekday: 'long' }); // ex: "sexta-feira"
  const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; // ex: "14:00"

  // Tenta usar o template configurado na empresa; se não houver, usa o fallback padrão
  const templates = MessagesTemplate.getTemplates(companyConfig);
  const message = templates.SuggestDateFromWaitingList || FALLBACK_TEMPLATE;

  // "Qualquer" indica que a vaga não é de um profissional específico — nesse
  // caso a linha "Profissional: " deve sumir do texto, igual a um valor vazio.
  const professionalName = resourceLabel && resourceLabel !== 'Qualquer' ? resourceLabel : '';

  const variables = {
    name: firstName,
    day,
    month,
    when: weekday,
    time,
    company_name: companyConfig?.Name || '',
    professional_name: professionalName,
    procedure_name: procedureName || '',
  };

  // Retorna o texto puro com \n — o chamador deve aplicar
  // MessagesTemplate.ConvertToWhatsAppText() antes de passar para SendWhatsAppMessage
  return applyTemplateVariables(message, variables, ['professional_name', 'procedure_name']);
}
